import {
  restorePendingUserMessageIdsForSession,
  stashPendingUserMessageIdsForSession,
} from "./chat-pending-user-messages.ts";

/**
 * JAISIU-types-sweep: typed chat-row shape carried through the
 * transcript caches and patch callbacks. Lives here (next to the
 * cache type) so anyone wiring a new patch callback gets a single
 * import surface for both the cache and its row schema.
 */
export type ChatStoredMessage = {
  id: string;
  serverId?: string;
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  status?: "pending" | "sent" | "failed" | "aborted";
  /** Optional client-side epoch-ms stamp; tests stub this as `Date.now()`. */
  createdAt?: number;
  model?: string;
  toolName?: string;
  toolCallId?: string;
};

/**
 * JAISIU-types-sweep: union of narrow (per-row typed) and wide
 * (legacy `unknown[]`) cache shapes. The narrow shape is the
 * preferred production shape; the wide shape remains because test
 * fixtures and a few legacy call sites still pass `unknown[]`. The
 * cache readers/writers internally cast to `unknown[]` for read and
 * to `readonly ChatStoredMessage[]` for write so each call site
 * stays simple.
 */
export type ChatMessagesBySessionCache =
  | Map<string, readonly ChatStoredMessage[]>
  | Map<string, unknown[]>;
export type PendingUserMessageIdsBySessionCache = Map<string, string[]>;

/**
 * How many sessions' transcripts stay resident.
 *
 * This cache existed as a tab-switch fast path and grew without limit: the
 * only per-key delete in the tree was one line in `refreshChat`, so closing
 * or archiving a tab freed nothing. Each entry is a FULL drained transcript
 * (see `drainOlderChatHistoryPages`), and transcripts carry media — so a
 * long shift across many sessions accumulated hundreds of MB and crashed the
 * tab.
 *
 * 8 matches the number of tabs an operator realistically keeps open. Beyond
 * that the cost of a `chat.history` round-trip is far cheaper than a tab
 * crash. The same LRU idiom already used by `composerStickyBySession` in
 * `chat-composer-shell.ts`.
 */
export const CHAT_MESSAGES_CACHE_MAX_SESSIONS = 8;

/**
 * Rough ceiling on a single cached transcript.
 *
 * A backstop for the pathological case: one session with a very long history
 * still only needs one copy, and the newest window is what the UI paints.
 * Older rows are dropped from the front, which preserves the newest
 * messages and therefore the optimistic-message anchor at the tail.
 */
export const CHAT_MESSAGES_CACHE_MAX_ROWS = 5_000;

/** Drop the oldest sessions until the cache is back within its bound. */
export function evictChatMessagesCache(
  cache: ChatMessagesBySessionCache | Map<string, unknown[]>,
): void {
  while (cache.size > CHAT_MESSAGES_CACHE_MAX_SESSIONS) {
    const oldest = (cache as Map<string, unknown[]>).keys().next().value;
    if (typeof oldest !== "string") {
      break;
    }
    (cache as Map<string, unknown[]>).delete(oldest);
  }
}

/** Read the cached transcript for a session (empty when uncached). */
export function readChatMessagesForSession(
  cache: ChatMessagesBySessionCache | undefined,
  sessionKey: string,
): readonly ChatStoredMessage[] {
  const key = sessionKey.trim();
  if (!key || !cache) {
    return [];
  }
  // JAISIU-types-sweep: the union cache type covers both
  // `readonly ChatStoredMessage[]` and `unknown[]`. Read through the
  // wider type so both variants surface here.
  const cached = (cache as ReadonlyMap<string, unknown[]>).get(key);
  return Array.isArray(cached) ? (cached as readonly ChatStoredMessage[]) : [];
}

/**
 * Write the cached transcript for a session, keeping the cache bounded.
 *
 * Evicts the least-recently-written session once the map exceeds its bound.
 * A write counts as a use, so the sessions an operator is actively cycling
 * between stay resident and the ones they left an hour ago do not.
 */
export function writeChatMessagesForSession(
  cache: ChatMessagesBySessionCache | undefined,
  sessionKey: string,
  messages: readonly unknown[],
): void {
  const key = sessionKey.trim();
  if (!key || !cache) {
    return;
  }
  const next =
    messages.length > CHAT_MESSAGES_CACHE_MAX_ROWS
      ? messages.slice(-CHAT_MESSAGES_CACHE_MAX_ROWS)
      : messages;
  // Re-insert so Map iteration order reflects recency for the eviction below.
  cache.delete(key);
  // JAISIU-types-sweep: the union ChatMessagesBySessionCache allows
  // both `Map<string, readonly ChatStoredMessage[]>` and
  // `Map<string, unknown[]>`; the spread is typed as the wider
  // `unknown[]` here so the cache.set accepts the value under both
  // variants without per-site casts.
  (cache as Map<string, unknown[]>).set(key, [...next]);
  evictChatMessagesCache(cache as Map<string, unknown[]>);
}

/** Apply a patch to the cached transcript and return the next array. */
export function patchChatMessagesForSession(
  cache: ChatMessagesBySessionCache | undefined,
  sessionKey: string,
  patch: (messages: readonly unknown[]) => unknown[],
): unknown[] {
  const key = sessionKey.trim();
  if (!key || !cache) {
    return patch([]);
  }
  const next = patch(readChatMessagesForSession(cache, key));
  // Same bound as `writeChatMessagesForSession` — a patch is a write.
  // The union cache type means `.set(...)` accepts `unknown[]` here
  // (the wider variant), and the result is shared with both variants.
  (cache as Map<string, unknown[]>).delete(key);
  (cache as Map<string, unknown[]>).set(key, next);
  evictChatMessagesCache(cache);
  return next;
}

/** Resolve live messages for merge/reconcile: active transcript or cache. */
export function resolveLiveMessagesForSession(state: {
  sessionKey: string;
  chatMessages: unknown[];
  chatMessagesBySession?: ChatMessagesBySessionCache;
}): unknown[] {
  const key = state.sessionKey.trim();
  if (!key) {
    return [];
  }
  if (Array.isArray(state.chatMessages) && state.chatMessages.length > 0) {
    // Defensive shallow copy so the cache can't be mutated by callers
    // that take the result by reference.
    return [...(state.chatMessages as unknown[])];
  }
  return readChatMessagesForSession(state.chatMessagesBySession, key) as unknown as unknown[];
}

function transcriptHasInFlightUserRow(
  messages: readonly unknown[],
  pendingIds: readonly string[],
): boolean {
  if (messages.length === 0 || pendingIds.length === 0) {
    return false;
  }
  const pendingSet = new Set(pendingIds);
  for (const message of messages) {
    if (!message || typeof message !== "object") {
      continue;
    }
    const record = message as { role?: unknown; pending?: unknown; id?: unknown };
    const id = typeof record.id === "string" ? record.id.trim() : "";
    if (!id || !pendingSet.has(id)) {
      continue;
    }
    if (record.role === "user") {
      return true;
    }
  }
  return false;
}

/** Resolve live messages for a specific session key (not necessarily active). */
export function resolveLiveMessagesForSessionKey(
  state: {
    sessionKey: string;
    chatMessages: unknown[];
    chatMessagesBySession?: ChatMessagesBySessionCache;
    pendingUserMessageIds?: string[];
    pendingUserMessageIdsBySession?: PendingUserMessageIdsBySessionCache;
  },
  targetSessionKey: string,
): unknown[] {
  const target = targetSessionKey.trim();
  if (!target) {
    return [];
  }
  const pendingIds =
    state.sessionKey.trim() === target
      ? (state.pendingUserMessageIds ?? [])
      : readPendingUserMessageIdsForSession(state.pendingUserMessageIdsBySession, target);
  if (state.sessionKey.trim() === target) {
    const active = Array.isArray(state.chatMessages) ? state.chatMessages : [];
    if (active.length > 0) {
      return active;
    }
    // Tab-switch / paint clears can zero the active transcript while the
    // per-session cache still holds an in-flight optimistic user row.
    const cached = readChatMessagesForSession(state.chatMessagesBySession, target);
    if (transcriptHasInFlightUserRow(cached, pendingIds)) {
      return cached as unknown[];
    }
    return active;
  }
  return readChatMessagesForSession(state.chatMessagesBySession, target) as unknown as unknown[];
}

/** Paint onto the active transcript only when the session is still active. */
export function paintChatMessagesIfActive(
  state: { sessionKey: string; chatMessages: unknown[] },
  targetSessionKey: string,
  messages: unknown[],
): void {
  if (state.sessionKey.trim() === targetSessionKey.trim()) {
    state.chatMessages = messages;
  }
}

export function readPendingUserMessageIdsForSession(
  cache: ReadonlyMap<string, string[]> | undefined,
  sessionKey: string,
): string[] {
  if (!cache) {
    return [];
  }
  return restorePendingUserMessageIdsForSession(cache, sessionKey);
}

export function writePendingUserMessageIdsForSession(
  cache: PendingUserMessageIdsBySessionCache | undefined,
  sessionKey: string,
  ids: readonly string[],
): void {
  if (!cache) {
    return;
  }
  stashPendingUserMessageIdsForSession(cache, sessionKey, ids);
}

/** Paint pending ids onto the active session only when it still matches. */
export function paintPendingUserMessageIdsIfActive(
  // JAISIU-types-sweep: `pendingUserMessageIds` is optional on
  // `ChatState` (host-managed; a chat controller fixture may not carry
  // it). This helper only ever WRITES the field — it never reads the
  // prior value — so accepting the optional shape changes nothing, it
  // just lets `controllers/chat.ts` pass a whole `ChatState` without
  // rebuilding a scratch object.
  state: { sessionKey: string; pendingUserMessageIds?: string[] },
  targetSessionKey: string,
  ids: string[],
): void {
  if (state.sessionKey.trim() === targetSessionKey.trim()) {
    state.pendingUserMessageIds = ids;
  }
}
